import React from 'react';
import BottomSheet from '../sheets/BottomSheet';
import RecapTierEditor from './RecapTierEditor';
import PreviousRecaps from './PreviousRecaps';
import RecapTierGroups from './RecapTierGroups';
import './RecapDrawer.css';

/**
 * The Lesson Recap as a right-hand drawer over the right track (I1 phase 2).
 *
 * Replaces the Lyrics | Recap segmented toggle. The design's reasoning: during
 * a lesson the right column should be the thing you LOOK at — lyrics, while she
 * sings — and the Recap is wrap-time writing that should not evict it. So the
 * lyrics stay mounted underneath and this slides over them; "Back to the
 * lesson" and the close button both dismiss it.
 *
 * IT REUSES BottomSheet rather than rolling a drawer. Everything that makes a
 * modal safe is already in there and easy to half-implement: role="dialog" +
 * aria-modal, a focus trap, Escape and scrim dismissal, body scroll lock, and
 * focus returned to whatever opened it. Only the geometry differs, and that is
 * `ld-sheet--drawer` in CSS — right-aligned, full height, 520px.
 *
 * THE COPY DELIBERATELY DOES NOT SAY "sent when you complete the lesson",
 * which is what the mockup's drawer says. The email is a CHECKBOX in the
 * wrap-up sheet — declinable, and absent entirely when no parent address is on
 * file (WrapUpSheet: "the checkbox keeps the send explicit and declinable").
 * Copy promising an automatic send would tell a teacher the family had been
 * written to when it had not, which is the more expensive way to be wrong. The
 * footer's email line below follows the same rule: "offers to email", and
 * "No parent email on file" when there is none.
 *
 * TEACHERS ONLY LEFT THIS DRAWER ON 2026-10-05 (design pass 4 P5.1 and P5.2):
 * the editors are Everyone and Parents, both inside "Goes home to the family"
 * (RecapTierGroups), and a pointer line under the save state says where the
 * private writing went, with `onOpenPrivate` opening the Private file (the
 * page closes this drawer first, so the rail or the private sheet is what the
 * teacher sees next). RecapTierGroups has the reasoning and the migration.
 *
 * `variant` is the ONLY difference between the desktop drawer and the iPad/phone
 * sheet (I1 phase 3): 'drawer' pins it to the right edge over the lyrics column,
 * 'sheet' leaves BottomSheet's own bottom geometry alone. Same component, same
 * copy, same tiers — because the thing that must not vary by width is which
 * audience a teacher thinks she is writing to.
 *
 * `previous` (2026-09-21) is the page-owned read of this student's earlier
 * recaps ({ lessons, isLoading, error } from usePreviousRecaps), rendered as a
 * read-only disclosure under the three editors. It lives HERE because this is
 * the one drawer every Recap entry point reaches, so the history is one tap
 * from any tab at any width without a second surface. Optional: a caller that
 * does not pass it gets the drawer exactly as before. `previousOpen` (design
 * pass 2) opens that disclosure on mount and scrolls it into view: the Last
 * lesson row's tap lands on the history, not on three editors above it.
 *
 * DESIGN PASS 2, CHANGES 9 AND 10 (2026-09-21, batch B). The body opens with
 * the save-state line the topbar button also shows (`saveState`, from
 * useRecapSaveState; nothing new is stored), so a teacher who has just typed
 * sees "Saving" turn into "Saved just now" where she is looking. The footer is
 * PINNED — BottomSheet's `footer` slot sits outside the scroll region — and
 * holds three things in this order: the email line (who the Wrap-up confirm
 * will offer to email, or that nobody is on file), **Complete lesson**, and
 * Back to the lesson. Complete lesson used to be a desktop-only topbar button
 * and a ghost "Wrap up lesson" at the foot of the notes column; it now lives
 * here, next to the writing it completes, and `onComplete` opens the EXISTING
 * Wrap-up confirm unchanged (tally, routine-draft ask, the declinable email
 * checkbox, "Complete anyway"). From any tab at any width the route is two
 * taps: Recap, then Complete lesson. Under 1200 the sheet's own
 * `padding-bottom: max(env(safe-area-inset-bottom), 8px)` keeps the footer
 * above the home indicator in the installed PWA.
 */
/** The "Teacher-only notes live in the Private file now" line shows until this date (P5.2: 30 days from 2026-10-05). */
export const PRIVATE_POINTER_UNTIL = Date.UTC(2026, 10, 5);

const RecapDrawer = ({
	recap,
	students = [],
	studentName = '',
	parentName = '',
	onClose,
	variant = 'drawer',
	previous = null,
	previousOpen = false,
	saveState = 'Not started',
	emailRecipient = '',
	emailRecipientName = '',
	onComplete = null,
	onOpenPrivate = null,
	now = null,
}) => {
	const saving = saveState === 'Saving';
	// Pass 4 P5.2: for a month after Teachers only left the drawer, say where it
	// went, with the file one tap away. After that the line retires itself.
	const showPrivatePointer = (now == null ? Date.now() : now) < PRIVATE_POINTER_UNTIL;
	const emailWho = emailRecipientName || emailRecipient;
	return (
		<BottomSheet
			title="Lesson Recap"
			titleId="ld-recap-drawer-title"
			className={`ld-recapdrawer${variant === 'drawer' ? ' ld-sheet--drawer' : ' ld-recapdrawer--sheet'}`}
			onClose={onClose}
			footer={
				<div className="ld-recapdrawer__foot">
					<p className="ld-recapdrawer__mail">
						<i className="fa-sharp fa-solid fa-envelope" aria-hidden="true" />
						{emailWho ? (
							<>
								<span className="ld-recapdrawer__mailtext">
									Completing offers to email <strong>{emailWho}</strong>
								</span>
								<span className="ld-recapdrawer__mailtiers">Everyone + Parents</span>
							</>
						) : (
							<span className="ld-recapdrawer__mailtext">No parent email on file, so nothing is emailed</span>
						)}
					</p>
					{onComplete && (
						<button type="button" className="ld-recapdrawer__complete" data-guide="recap-complete" onClick={onComplete}>
							<i className="fa-sharp fa-solid fa-flag-checkered" aria-hidden="true" />
							Complete lesson
						</button>
					)}
					<button type="button" className="ld-recapdrawer__back" onClick={onClose}>
						<i className="fa-sharp fa-solid fa-arrow-left" aria-hidden="true" /> Back to the lesson
					</button>
				</div>
			}>
			<p className={`ld-recapdrawer__saved${saving ? ' ld-recapdrawer__saved--saving' : ''}`} aria-live="polite">
				<i className={`fa-sharp fa-solid ${saving ? 'fa-rotate' : 'fa-check'}`} aria-hidden="true" />
				<span className="ld-recapdrawer__savedstate">{saveState}</span>
				<span className="ld-recapdrawer__savednote">Saved as you type; completing the lesson offers to email the family the Everyone and Parents tiers.</span>
			</p>

			{showPrivatePointer && (
				<p className="ld-recapdrawer__private">
					<i className="fa-sharp fa-solid fa-lock" aria-hidden="true" />
					<span className="ld-recapdrawer__privatetext">Teacher-only notes live in the Private file now.</span>
					{onOpenPrivate && (
						<button type="button" className="ld-recapdrawer__privateopen" onClick={onOpenPrivate}>
							Open it
						</button>
					)}
				</p>
			)}

			<div className="ld-recapdrawer__tiers">
				<RecapTierGroups
					renderTier={(tier) => (
						<RecapTierEditor
							key={tier.key}
							tier={tier}
							content={recap.tiers[tier.visibility]?.content}
							onUpdate={(payload) => recap.handleTierUpdate(tier.visibility, payload)}
							students={students}
							studentName={studentName}
							parentName={parentName}
							unlinked={recap.noStudentLinked}
						/>
					)}
				/>
			</div>

			{previous && (
				<PreviousRecaps
					lessons={previous.lessons}
					isLoading={previous.isLoading}
					error={previous.error}
					defaultOpen={previousOpen}
					scrollIntoViewOnMount={previousOpen}
				/>
			)}
		</BottomSheet>
	);
};

export default RecapDrawer;
